import { type FieldFunctionOptions } from "@apollo/client";
import { type FieldMergeFunction } from "@apollo/client/cache/inmemory/policies";

import { type PayoutStatsResponse } from "../../../gql-generated/types";

type KeyArgs = (string | KeyArgs)[];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isString = (value: unknown): value is string => typeof value === "string";

const getInputCursor = (args: FieldFunctionOptions["args"]): string | undefined => {
    const input: unknown = args?.["input"];
    if (!isRecord(input)) return undefined;

    const lensList: unknown = input["lensList"];
    if (isRecord(lensList) && isString(lensList["cursor"])) return lensList["cursor"];

    const history: unknown = input["history"];
    if (isRecord(history) && isString(history["cursor"])) return history["cursor"];

    return undefined;
};

interface PayoutStatsCacheTypePolicy {
    keyArgs: KeyArgs;
    merge: FieldMergeFunction<PayoutStatsResponse, PayoutStatsResponse>;
}

const payoutStatsTypePolicy: PayoutStatsCacheTypePolicy = {
    keyArgs: [
        "input",
        [
            "startDate",
            "endDate",
            "countryFilters",
            "unlockableId",
            "payoutProgram",
            "lensList",
            ["sortBy", "sortOrder", "limit"],
            "history",
            ["granularity", "limit"],
            "summary",
        ],
    ],
    merge: (existing, incoming, { args }) => {
        const hasCursor = getInputCursor(args);

        if (!existing || !hasCursor) {
            return incoming;
        }

        return {
            ...incoming,
            lensList:
                incoming.lensList && existing.lensList
                    ? {
                          ...incoming.lensList,
                          lenses: [...existing.lensList.lenses, ...incoming.lensList.lenses],
                      }
                    : incoming.lensList,
            history:
                incoming.history && existing.history
                    ? {
                          ...incoming.history,
                          periods: [...existing.history.periods, ...incoming.history.periods],
                      }
                    : incoming.history,
        };
    },
};

export const payoutStatsCacheConfig = {
    payoutStats: payoutStatsTypePolicy,
};
